import { Injectable } from '@angular/core';
import { apiUrl, BaseUrls } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { AuthUser } from '@app/core/models/auth-user';
import { isEnterprise, isServiceProvider, UserLevel } from '@app/core/models/user-level';
import { findUserLevel } from '@app/menu/utils/find-user-level';
import { RouterService } from '@app/routing/router.service';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { portalError } from '@app/utils/error/portal-error';
import { loggerFor } from '@app/utils/logger';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { first, map, mergeMap, take, tap } from 'rxjs/operators';

export type ApiUrlParams = {
  serviceProviderId?: number | string;
  enterpriseId?: number | string;
  userId?: number | string;
};

@Injectable({
  providedIn: 'root',
})
export class Utils {
  private logger = loggerFor('Utils');

  constructor(
    private authenticationService: AuthenticationService,
    private routerService: RouterService,
    private translateService: TranslateService
  ) {}

  /**
   * Create an api url
   *
   * @param url
   * @param params
   * @param isUserContext
   */
  apiUrl(
    url: string | { [key: string]: string },
    params: ApiUrlParams = {},
    isUserContext?: boolean
  ): Observable<string> {
    /**
     * Process the given url based on the user level of the user and the userLevel this route requires
     * @param url
     * @param userLevel
     * @param isUserContext
     */
    const processUrl = (
      url: string | { [key: string]: string },
      userLevel: UserLevel,
      isUserContext: boolean
    ) => {
      if (typeof url === 'string') {
        if (isServiceProvider(this.authenticationService.authUser)) {
          switch (userLevel) {
            case UserLevel.EndUser:
              return (isUserContext ? '' : BaseUrls.ServiceProvider.EndUser) + url;
            case UserLevel.EnterpriseAdmin:
              return (isUserContext ? '' : BaseUrls.ServiceProvider.Enterprise) + url;
            default:
              return BaseUrls.ServiceProvider.ServiceProvider + url;
          }
        } else if (isEnterprise(this.authenticationService.authUser)) {
          switch (userLevel) {
            case UserLevel.EndUser:
              return (isUserContext ? '' : BaseUrls.Enterprise.EndUser) + url;
            default:
              if (isUserContext) {
                return url;
              } else {
                return BaseUrls.Enterprise.Enterprise + url;
              }
          }
        } else {
          return BaseUrls.EndUser + url;
        }
      } else {
        const authUser = this.authenticationService.authUser;

        if (isServiceProvider(authUser) && url[UserLevel.ServiceProviderAdmin.toString()] !== undefined) {
          return url[UserLevel.ServiceProviderAdmin.toString()];
        } else if (isEnterprise(authUser) && url[UserLevel.EnterpriseAdmin.toString()] !== undefined) {
          return url[UserLevel.EnterpriseAdmin.toString()];
        } else {
          return url[UserLevel.EndUser];
        }
      }
    };

    return this.routerService.currentRouteData$.pipe(
      take(1),
      map((current) => {
        const routeParams = current.params;

        let parameters: ApiUrlParams = {
          serviceProviderId: params.serviceProviderId || routeParams.serviceProviderId || '',
          enterpriseId: params.enterpriseId || routeParams.enterpriseId || '',
          userId: params.userId || routeParams.userId || '',
        };

        const processedUrl: string = processUrl(url, findUserLevel(current), isUserContext);

        if (processedUrl == null) {
          this.logger.error(
            'Could not create a url for the given input, url=',
            url,
            ', params=',
            params,
            'isUserContext=',
            isUserContext
          );

          throw portalError({
            cause: new Error('Could not create a URL, see console for more information about the error.'),
            id: 'utils:apiUrl',
            i18n: 'Utils.apiUrl()',
          });
        }

        this.logger.debug(`Created url ${processedUrl} from raw url`, url, 'and params', params);

        return apiUrl(
          processedUrl
            .replace(/:serviceProviderId/g, parameters.serviceProviderId.toString())
            .replace(/:enterpriseId/g, parameters.enterpriseId.toString())
            .replace(/:userId/g, parameters.userId.toString())
        );
      })
    );
  }

  /**
   * Creates a URL to the common enterprise API
   * @param url
   */
  commonApiUrl(url: string) {
    return this.routerService.currentRouteData$.pipe(
      take(1),
      map((current) => {
        const routeParams = current.params;

        const parameters: ApiUrlParams = {
          serviceProviderId: routeParams.serviceProviderId || '',
          enterpriseId: routeParams.enterpriseId || '',
          userId: routeParams.userId || '',
        };

        let realUrl: string;

        const activeUserLevel = findUserLevel(current);

        if (this.authenticationService.userIsServiceProviderAdmin()) {
          if ([UserLevel.EnterpriseAdmin, UserLevel.EndUser].includes(activeUserLevel)) {
            realUrl = BaseUrls.ServiceProvider.Enterprise + url;
          } else {
            realUrl = BaseUrls.ServiceProvider.ServiceProvider + url;
          }
        } else if (this.authenticationService.userIsEnterpriseAdmin()) {
          realUrl = BaseUrls.Enterprise.Enterprise + url;
        } else {
          this.logger.error('Illegal access to common enterprise API: ', url);
          realUrl = BaseUrls.EndUser + url;
        }

        return apiUrl(
          realUrl
            .replace(/:serviceProviderId/g, parameters.serviceProviderId.toString())
            .replace(/:enterpriseId/g, parameters.enterpriseId.toString())
            .replace(/:userId/g, parameters.userId.toString())
        );
      })
    );
  }

  ifServiceProvider<T>(
    thenFunction?: (user: AuthUser) => Observable<T>,
    elseFunction?: (user: AuthUser) => Observable<T>
  ): Observable<T> {
    return this.getAuthUser().pipe(
      mergeMap((user) => (isServiceProvider(user) ? thenFunction(user) : elseFunction(user)))
    );
  }

  ifEnterprise<T>(
    thenFunction?: (user: AuthUser) => Observable<T>,
    elseFunction?: (user: AuthUser) => Observable<T>
  ): Observable<T> {
    return this.getAuthUser().pipe(
      mergeMap((user) => (isEnterprise(user) ? thenFunction(user) : elseFunction(user)))
    );
  }

  mapIfEnterprise<T>(thenFunction?: (user: AuthUser) => T, elseFunction?: (user: AuthUser) => T) {
    return this.getAuthUser().pipe(
      map((user) => (isEnterprise(user) ? thenFunction(user) : elseFunction(user)))
    );
  }

  /**
   * Takes either an i18n key or an array of i18n keys and returns an observable that contains an
   * array of translated values that is updated whenever the underlying translation changes (e.g.
   * because the user switched the language).
   *
   * @param value i18n key or array of i18n keys
   */
  public streamTranslatedTypeahead<T>(
    value: TypeaheadEntry<T> | TypeaheadEntry<T>[]
  ): Observable<TypeaheadEntry<T>[]> {
    if (!Array.isArray(value)) {
      value = [value];
    }

    // Combine observables that contain translations
    return combineLatest(
      value.map((value) => {
        // Stream translation
        return this.translateService.stream(value.label).pipe(
          // Map back to the typeahead entry
          map((translation) => {
            return {
              ...value,
              label: translation,
            };
          })
        );
      })
    );
  }

  subscribeIfServiceProvider(func): void {
    this.getAuthUser().pipe(tap(func));
  }

  /**
   * Takes either an i18n key or an array of i18n keys and returns an array of translated values.
   *
   * @param value i18n key or array of i18n keys
   */
  public translateTypeahead<T>(value: TypeaheadEntry<T> | TypeaheadEntry<T>[]): TypeaheadEntry<T>[] {
    if (!Array.isArray(value)) {
      value = [value];
    }

    return value.map((value) => {
      return {
        ...value,
        label: this.translateService.instant(value.label),
      };
    });
  }

  routeBasedApiUrl(url: string, userLevel?: UserLevel): Observable<string> {
    return this.routerService.currentRouteData$.pipe(
      first(),
      map((routeData) => {
        const userLevelUrl = this.decideApiUserLevel(url, userLevel ?? findUserLevel(routeData));
        const parameters: ApiUrlParams = {
          serviceProviderId: routeData.params['serviceProviderId'] ?? '',
          enterpriseId: routeData.params['enterpriseId'] ?? '',
          userId: routeData.params['userId'] ?? '',
        };

        return userLevelUrl
          .replace(/:serviceProviderId/g, parameters.serviceProviderId.toString())
          .replace(/:enterpriseId/g, parameters.enterpriseId.toString())
          .replace(/:userId/g, parameters.userId.toString());
      })
    );
  }

  private getAuthUser(): Observable<AuthUser> {
    return this.authenticationService.authUser$;
  }

  private decideApiUserLevel(url: string, activeUserLevel: UserLevel): string {
    let adjustedUrl: string;

    if (this.authenticationService.userIsServiceProviderAdmin()) {
      if (activeUserLevel === UserLevel.EndUser) {
        adjustedUrl = apiUrl(BaseUrls.ServiceProvider.EndUser + url);
      } else if (activeUserLevel === UserLevel.EnterpriseAdmin) {
        adjustedUrl = apiUrl(BaseUrls.ServiceProvider.Enterprise + url);
      } else if (activeUserLevel === UserLevel.ServiceProviderAdmin) {
        adjustedUrl = apiUrl(BaseUrls.ServiceProvider.ServiceProvider + url);
      } else {
        adjustedUrl = apiUrl(url);
      }
    } else if (this.authenticationService.userIsEnterpriseAdmin()) {
      if (activeUserLevel === UserLevel.EndUser) {
        adjustedUrl = apiUrl(BaseUrls.Enterprise.EndUser + url);
      } else if (activeUserLevel === UserLevel.EnterpriseAdmin) {
        adjustedUrl = apiUrl(BaseUrls.Enterprise.Enterprise + url);
      }
    } else if (this.authenticationService.userIsEndUser()) {
      adjustedUrl = apiUrl(BaseUrls.EndUser + url);
    }

    if (adjustedUrl == null) {
      throw portalError({
        cause: new Error(
          `Could not decide a user level for the given url "${url}" and active user level "${activeUserLevel}"`
        ),
        id: 'utils-service:decide-api-user-level',
      });
    }

    return adjustedUrl;
  }
}
